import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useSnackbar } from "notistack";
import apiCall from "../../helpers/api";
import { connect } from "react-redux";
import { loginUser } from "../../services/user/actions";
import {
  RESET_PASSWORD_URL,
  VERIFY_EMAIL_FOR_RESET_PASSWORD_URL,
} from "../../configs/url";
import OTPInput from "react-otp-input";
import { useTranslation } from "react-i18next";
// import { getSettings } from "../../services/settings/actions";

function ResetPassword(props) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [recaptchaVal, setRecaptchaVal] = useState(null);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.title = "Login | KPG Partner";
  }, []);

  const handleEmailVerify = async () => {
    enqueueSnackbar("Verifying email...", {
      variant: "info",
    });
    apiCall({
      method: "POST",
      url: VERIFY_EMAIL_FOR_RESET_PASSWORD_URL,
      data: {
        email: email,
        recaptcha: recaptchaVal,
      },
    })
      .then((response) => {
        if (response.data.success) {
          enqueueSnackbar("A verification code has been sent to your email.", {
            variant: "success",
          });
          setOtpSent(true);
        } else {
          enqueueSnackbar("Failed to verify email. Please try again.", {
            variant: "error",
          });
        }
      })
      .catch((error) => {
        enqueueSnackbar(error.response?.data?.message ?? "An error occurred", {
          variant: "error",
        });
        console.error("Email verification error:", error);
      });
  };

  const handleResetPassword = async () => {
    if (!otp) {
      enqueueSnackbar("Please enter the verification code.", {
        variant: "error",
      });
      return;
    }
    if (password !== confirmPassword) {
      enqueueSnackbar("Passwords do not match.", { variant: "error" });
      return;
    }
    enqueueSnackbar("Resetting password...", {
      variant: "info",
    });
    apiCall({
      method: "POST",
      url: RESET_PASSWORD_URL,
      data: {
        password: password,
        confirm_password: confirmPassword,
        email: email,
        otp: otp,
      },
    })
      .then((response) => {
        if (response?.data && response.data.success) {
          enqueueSnackbar("Password reset successfully!", {
            variant: "success",
          });
          navigate("/login");
        } else {
          enqueueSnackbar("Failed to reset password. Please try again.", {
            variant: "error",
          });
        }
      })
      .catch((error) => {
        enqueueSnackbar(error.response?.data?.message ?? "An error occurred", {
          variant: "error",
        });
        console.error("Password reset error:", error);
      });
  };

  useEffect(() => {
    if (props?.user && props?.user?.auth_token) {
      navigate("/dashboard");
    }
  }, [props?.user]);

  useEffect(() => {
    if (props?.user && props?.user?.auth_token) {
      navigate("/dashboard");
    }
  }, [props]);

  const onRecaptchaChange = (value) => {
    setRecaptchaVal(value);
  };

  const handleSocialLogin = async (provider) => {
    window.location.href = `${SOCIAL_LOGIN_API_URL}/${provider}`;
  };

  return (
    <>
      <div className="login-wrapper">
        <div className="row justify-content-center align-items-center">
          <div className="mt-5 row">
            <div className="text-center">
              <img
                src="/images/kpg-logo.png"
                alt="Logo"
                className="login-logo img-fluid"
              />
            </div>
          </div>

          <div className="login-card card col-xl-3 col-lg-4 col-10 shadow">
            <div className="card-body">
              {!otpSent && (
                <>
                  <div className="row login-header">
                    <h5>{t("reset_title")}</h5>
                    <span className="text-muted">{t("reset_subtitle")}</span>
                  </div>

                  <div className="row mt-4">
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        {t("auth_email")}
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        placeholder="Enter your email"
                        required
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    <div className="recaptcha-container">
                      <ReCAPTCHA
                        sitekey="6LdOkaYtAAAAAK-UW8BmQ4JKXjRIQH8rakx1sZCa"
                        onChange={onRecaptchaChange}
                        className="g-recaptcha"
                      />
                    </div>
                    <button
                      onClick={handleEmailVerify}
                      className="btn btn-primary w-100"
                      disabled={!recaptchaVal}
                    >
                      {t("reset_verify_btn")}
                    </button>
                  </div>
                </>
              )}
              {otpSent && (
                <>
                  <div className="row login-header">
                    <h5>{t("reset_enter_otp_title")}</h5>
                    <span className="text-muted">
                      {t("reset_enter_otp_subtitle")}
                    </span>
                  </div>

                  <div className="row mt-4">
                    <div className="mb-3">
                      <label htmlFor="otp" className="form-label">
                        {t("reset_otp")}
                      </label>
                      <OTPInput
                        value={otp}
                        onChange={(value) => setOtp(value)}
                        numInputs={6}
                        inputType={true}
                        inputStyle={{
                          width: "50px",
                          height: "50px",
                          margin: "0 5px",
                          fontSize: "24px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                        }}
                        focusStyle={{
                          borderColor: "#007bff",
                          outline: "none",
                        }}
                        isInputNum={true}
                        shouldAutoFocus={true}
                        renderSeparator={<span>&nbsp;</span>}
                        renderInput={(props) => (
                          <input
                            {...props}
                            type="number" // This ensures numeric input and brings up the numeric keyboard on mobile devices
                            inputMode="numeric" // This brings up the numeric keyboard on mobile devices
                          />
                        )}
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="password" className="form-label">
                        {t("reset_password")}
                      </label>
                      <input
                        type="password"
                        className="form-control"
                        id="password"
                        placeholder={t("reset_password")}
                        required
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="confirmPassword" className="form-label">
                        {t("reset_confirm_password")}
                      </label>
                      <input
                        type="password"
                        className="form-control"
                        id="confirmPassword"
                        placeholder={t("reset_confirm_password")}
                        required
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </div>
                    <button
                      onClick={handleResetPassword}
                      className="btn btn-primary w-100"
                    >
                      {t("reset_password_btn")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user.user,
  // settings: state.settings.settings,
});

const mapDispatchToProps = {
  loginUser,
  // getSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResetPassword);
